import * as Sentry from '@sentry/vue';
import { ofetch, type FetchError } from 'ofetch';
import { ApiError } from './api-error';
import { apiOrigin } from './api-origin';
import { readCookieByPrefix } from '~/utils/cookies';

type ApiOpts = {
  method?: string;
  body?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
};

// ---------------------------------------------------------------------------
// Pluggable hooks — registered by stores in later phases
// ---------------------------------------------------------------------------

type VoidCallback = () => void;

let onUnauthorized: VoidCallback = () => {
  navigateTo('/login');
};
let onConflict: VoidCallback = () => {};

export function setUnauthorizedHandler(handler: VoidCallback) {
  onUnauthorized = handler;
}

export function setConflictHandler(handler: VoidCallback) {
  onConflict = handler;
}

// ---------------------------------------------------------------------------
// Bearer token — stored in localStorage
// ---------------------------------------------------------------------------

const TOKEN_KEY = 'arvee_access_token';

export function getAccessToken(): string | null {
  if (import.meta.server) return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setAccessToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAccessToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// ---------------------------------------------------------------------------
// Error normalization
// ---------------------------------------------------------------------------

function normalizeError(err: unknown, requestId: string): ApiError {
  const fe = err as FetchError;
  const status = fe.response?.status ?? 0;

  if (status === 0) {
    return new ApiError(
      0,
      'A network error occurred. Please check your connection.',
      requestId,
    );
  }

  const body = fe.data as Record<string, unknown> | undefined;
  const message =
    typeof body?.message === 'string'
      ? body.message
      : fe.message || 'An unexpected error occurred';

  if (status === 422 && body?.errors) {
    return new ApiError(
      status,
      message,
      requestId,
      body.errors as Record<string, string[]>,
    );
  }

  if (status === 429) {
    return new ApiError(
      status,
      'Too many requests. Please wait a moment and try again.',
      requestId,
    );
  }

  if (status >= 500) {
    return new ApiError(
      status,
      'Something went wrong on our end. Please try again later.',
      requestId,
    );
  }

  return new ApiError(status, message, requestId);
}

// ---------------------------------------------------------------------------
// Matomo visitor-ID forwarding — read first 16-hex segment from `_pk_id.8.*`
// cookie so backend-emitted events tie back to this browser's visit.
// ---------------------------------------------------------------------------

function readMatomoVisitorId(): string | null {
  // First prefix match only — an invalid value nulls the header out rather than
  // falling through to a later `_pk_id.8.*` cookie from another domain scope.
  const value = readCookieByPrefix('_pk_id.8.');
  if (value === null) return null;

  const visitorId = value.split('.')[0];
  return visitorId && /^[0-9a-f]{16}$/i.test(visitorId) ? visitorId : null;
}

// ---------------------------------------------------------------------------
// Core fetch — authenticated, with Bearer token, request ID
// ---------------------------------------------------------------------------

async function apiFetch<T>(
  path: string,
  opts: ApiOpts & { method: string },
): Promise<T> {
  const requestId = crypto.randomUUID();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': requestId,
    'X-Arvee-Surface': 'events',
  };

  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const visitorId = readMatomoVisitorId();
  if (visitorId) {
    headers['X-Matomo-Visitor-Id'] = visitorId;
  }

  try {
    return await ofetch<T>(path, {
      method: opts.method,
      body: opts.body as BodyInit,
      params: opts.params,
      baseURL: apiOrigin(),
      credentials: 'omit',
      headers: { ...headers, ...opts.headers },
      timeout: opts.method === 'GET' ? 15000 : 30000,
    });
  } catch (err) {
    const apiError = normalizeError(err, requestId);

    if (apiError.isUnauthorized) {
      clearAccessToken();
      onUnauthorized();
    } else if (apiError.isConflict) {
      onConflict();
    }

    if (import.meta.client) {
      Sentry.getCurrentScope().setTag('request_id', requestId);
    }

    throw apiError;
  }
}

// ---------------------------------------------------------------------------
// Public fetch — unauthenticated SSR-safe fetch (no cookies, no token)
// ---------------------------------------------------------------------------

async function publicFetch<T>(path: string, opts?: ApiOpts): Promise<T> {
  const requestId = crypto.randomUUID();

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': requestId,
  };

  try {
    return await ofetch<T>(path, {
      params: opts?.params,
      baseURL: apiOrigin(),
      credentials: 'omit',
      headers: { ...headers, ...opts?.headers },
      timeout: 15000,
    });
  } catch (err) {
    const apiError = normalizeError(err, requestId);

    if (import.meta.client) {
      Sentry.getCurrentScope().setTag('request_id', requestId);
    }

    throw apiError;
  }
}

// ---------------------------------------------------------------------------
// useApi composable — returns typed convenience methods
// ---------------------------------------------------------------------------

export function useApi(basePath: string = '') {
  return {
    get: <T>(path: string, opts?: ApiOpts) =>
      apiFetch<T>(basePath + path, { ...opts, method: 'GET' }),
    post: <T>(path: string, opts?: ApiOpts) =>
      apiFetch<T>(basePath + path, { ...opts, method: 'POST' }),
    put: <T>(path: string, opts?: ApiOpts) =>
      apiFetch<T>(basePath + path, { ...opts, method: 'PUT' }),
    patch: <T>(path: string, opts?: ApiOpts) =>
      apiFetch<T>(basePath + path, { ...opts, method: 'PATCH' }),
    del: <T>(path: string, opts?: ApiOpts) =>
      apiFetch<T>(basePath + path, { ...opts, method: 'DELETE' }),
    publicGet: <T>(path: string, opts?: ApiOpts) =>
      publicFetch<T>(basePath + path, opts),
  };
}
